import _ from 'lodash';
import React from 'react';
import autobind from 'autobind-decorator';
import { observer, inject } from 'mobx-react';
import { toJS } from 'mobx';
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';

import AccountCreationForm from './account-creation-form';
import AuditionForm from './audition-form';

const DFLT_MODAL_STYLE = {
	base: 'modal artist-sign-up',
	afterOpen: 'modal-after-open',
	beforeClose: 'modal-before-close'
};

const DFLT_OVERLAY_STYLE = {
	base: 'modal-overlay',
	afterOpen: 'modal-overlay-after-open',
	beforeClose: 'modal-overlay-before-close'
};

const FIELD_KEY_MAP = {
	firstName: 'user.firstName',
	lastName: 'user.lastName',
	name: 'artist.name',
	email: 'user.email',
	password: 'user.password',
	confirmPassword: 'user.confirmPassword',
	phoneNumber: 'user.phoneNumber',
	genre: 'artist.genre',
	paymentServiceID: 'artist.paymentServiceID',
	dob: 'user.dob',
	hfmPicksOptIn: 'artist.hfmPicksOptIn',
	location: 'user.location'
};

@inject('formStore')
@observer
class ProgressInfo extends React.Component {
	render () {
		const {
			formStore: {
				validation: {
					profileImage = false,
					auditionBioForm = false,
					auditionSongs = false,
					accountCreationForm = false
				}
			}
		} = this.props;

		const cellClassName = 'cell small-3 padding-bottom-0 text-right';

		return (
			<div className="grid-x grid-padding-x grid-padding-y artist-sign-up-progress">
				<div className={ `${ cellClassName } ${ accountCreationForm ? 'highlight-text' : 'disabled-text' }` }>
					<span className="fas fa-check"></span> Info
				</div>
				<div className={ `${ cellClassName } ${ auditionBioForm ? 'highlight-text' : 'disabled-text' }` }>
					<span className="fas fa-check"></span> Bio
				</div>
				<div className={ `${ cellClassName } ${ profileImage ? 'highlight-text' : 'disabled-text' }` }>
					<span className="fas fa-check"></span> Image
				</div>
				<div className={ `${ cellClassName } ${ auditionSongs ? 'highlight-text' : 'disabled-text' }` }>
					<span className="fas fa-check"></span> Songs
				</div>
			</div>
		);
	}
}

@inject('artistStore', 'userStore', 'songsStore', 'formStore')
@observer
class AddBandModal extends React.Component {
	constructor (props) {
		super(props);

		this.state = {
			savingMessage: '',
			modalOpen: false,
			modalPage: 'step1',
			bio: '',
			formIsValid: false
		};

		this.props.formStore.setValidation({
			profileImage: false,
			auditionBioForm: false,
			auditionSongs: false,
			accountCreationForm: false
		});
	}

	componentDidMount () {
		document.addEventListener('keypress', this.onKeyPress);
	}

	componentWillUnmount () {
		document.removeEventListener('keypress', this.onKeyPress);
	}

	render () {
		const {
			modalContentLabel = 'Artist Sign Up',
			modalClassName = DFLT_MODAL_STYLE,
			modalOverlayClassName = DFLT_OVERLAY_STYLE,
			buttonClassName = 'button hollow secondary',
			buttonLabel = 'Sign Up With Email',
			iconClassName
		} = this.props;

		return (
			<div className="artist-sign-up">
				<ReactModal
					appElement={ window.document.querySelector('#app') }
					isOpen={ this.state.modalOpen }
					shouldCloseOnOverlayClick={ true }
					contentLabel={ modalContentLabel }
					className={ DFLT_MODAL_STYLE }
					overlayClassName={ DFLT_OVERLAY_STYLE }
				>
					<h4 className="padding-left-2 padding-top-1">{ modalContentLabel }</h4>
					<ProgressInfo />
					{ this.renderModalContent() }
				</ReactModal>

				<button
					data-ta="addBandBtn"
					className={ buttonClassName }
					onClick={ this.handleOpenModal }
				>
					{
						_.isString(iconClassName) ? <span className={ iconClassName }></span> : null
					}
					{ buttonLabel }
				</button>
			</div>
		);
	}

	renderModalContent () {
		const { artistStore } = this.props;

		console.log('[renderModalContent]', artistStore.isLoading);
		if (artistStore.isLoading) {

			return (
				<div className="loading-spinner large text-center" style={{
					height: '65vh'
				}}>{ this.state.savingMessage }</div>
			);
		}

		return (
			<div className="modal-content" data-ta="addBandModal" style={{backgroundColor: 'black', color: 'white !important'}}>
				<AccountCreationForm />

				<AuditionForm
					adminMode={ this.props.adminMode }
					onBioChange={ this.onBioChange }
					handleImageCrop={ this.handleImageCrop }
				/>

				{ this.renderButtons() }
			</div>
		);
	}

	renderButtons () {
		return (
			<div className="modal-button-group">
				<button
					data-ta="closeAddBandModalBtn"
					className="button clear secondary"
					onClick={ this.handleCloseModal }
				>
					Close Modal
				</button>
				<button
					data-ta="saveAddBandModalBtn"
					disabled={ this.submitDisabled() }
					className="button clear padding-right-0 primary-color"
					onClick={ this.onSubmitClick }
				>
					Submit
				</button>
			</div>
		);
	}

	submitDisabled () {
		const {
			formStore: {
				validation: {
					profileImage = false,
					auditionBioForm = false,
					auditionSongs = false,
					accountCreationForm = false
				}
			}
		} = this.props;

		const submitDisabled =
			profileImage !== true ||
			auditionBioForm !== true ||
			auditionSongs !== true ||
			accountCreationForm !== true;

		return submitDisabled;
	}

	@autobind
	handleImageCrop (cropResult) {
		const { formStore, userStore } = this.props;

		formStore.setValidation({
			profileImage: true
		});

		userStore.set({
			value: cropResult,
			key: 'user.uploadImgData'
		});
	}

	@autobind
	handleOpenModal () {
		const { artistStore, clearStoreData } = this.props;

		if (clearStoreData) {

			artistStore.setCurrent(null);
		}

		this.setState({
			modalOpen: true
		});
	}

	@autobind
	handleCloseModal () {
		this.setState({
			modalPage: 'step1',
			modalOpen: false
		});
	}

	@autobind
	onKeyPress ({ key }) {
		if ((/enter/i).test(key) && this.submitDisabled() !== true) {

			this.onSubmitClick();
		}
	}

	@autobind
	async onSubmitClick () {
		const {
			userStore,
			formStore,
			artistStore,
			songsStore,
			adminMode,
			disableAutoLogin,
			clearStoreData
		} = this.props;

		const { email } = userStore.data.user.toPOJO();

		if (_.isString(email)) {

			const { data } = await userStore.checkEmail(email);

			if (data.exists) {

				toast.error(`${email} already exists.`);
				return;
			}
		}

		let errorMsg = [];

		const isValid = formStore.validateFields(_.values(FIELD_KEY_MAP), (key) => {
			const store = (/^user/).test(key) ? userStore : artistStore;
			const value = store.get(key);

			if (key === FIELD_KEY_MAP.confirmPassword) {

				return {
					confirmPassword: value,
					password: userStore.get(FIELD_KEY_MAP.password)
				};
			}

			return value;
		});

		if (isValid !== true) {

			const message = 'Please enter all required fields';

			toast.error(message);
			return;
		}

		if (userStore.get(FIELD_KEY_MAP.password) !== userStore.get(FIELD_KEY_MAP.confirmPassword)) {

			toast.error('Please make sure your confimation password matches your password.');
			return;
		}

		if (errorMsg.length > 0) {

			toast.error(errorMsg.join(' '));
			return;
		}

		this.setState({
			savingMessage: 'Saving profile...'
		});

		const userObj = userStore.data.user.toPOJO();
		await artistStore.createArtist(
			artistStore.data.artist.toPOJO(),
			userObj,
			songsStore.artistSongDirectory
		);

		if (disableAutoLogin) {

			artistStore.fetchItems();
			userStore.fetchItems();
			this.handleCloseModal();
			return;
		}

		this.setState({
			savingMessage: 'Log in...'
		});

		// Log user in and route to Band CMS
		userStore.checkLogin({
			sendRedirect: true,
			...userObj
		});
	}
};


export default AddBandModal;
